import joi from 'joi';

import { POLICY_ID_PATTERN } from '../rbac/policyId';
import type { TableResponseBody } from '../table/Table';
import type { Branded, PartialRequireAtLeastOne } from '../types';
import { identity } from '../utils/functions';
import { slugSchema } from '../validation';
import type { PolicyId } from './Policy';

export type RoleBase = {
  name: string;
  description: string;
  isAdmin: boolean;
  isSystem: boolean;
};

export type RoleId = Branded<string, 'roleId'>;

export type Role = RoleBase & {
  id: RoleId;
};

export type RoleWithPolicies = Role & {
  policyIds: PolicyId[];
};

type RoleRequestBodyBase = Omit<
  RoleWithPolicies,
  'id' | 'isAdmin' | 'isSystem'
>;

const roleRequestBodyBaseSchema = joi.object().keys(
  identity<joi.SchemaMap<RoleRequestBodyBase>>({
    name: slugSchema.required(),
    description: joi.string().allow('').required(),
    policyIds: joi.array().items(joi.string()).required(),
  }),
);

// POST /roles
export type RolesPostRequestBody = RoleRequestBodyBase;
export const rolesPostRequestBodySchema = roleRequestBodyBaseSchema;
export type RolesPostResponseBody = RoleWithPolicies;

// GET /roles
export type RolesGetResponseBody = TableResponseBody<Role>;

// GET /roles/:roleId
export type RoleGetResponseBody = RoleWithPolicies;

// PATCH /roles/:roleId
export type RolePatchRequestBody =
  PartialRequireAtLeastOne<RoleRequestBodyBase>;
export const rolePatchRequestBodySchema = roleRequestBodyBaseSchema
  .fork(['description', 'name', 'policyIds'], (e) => e.optional())
  .min(1);
export type RolePatchResponseBody = RoleWithPolicies;

// PUT /roles/:roleId
export type RolePutRequestBody = RoleRequestBodyBase;
export const rolePutRequestBodySchema = roleRequestBodyBaseSchema;
export type RolePutResponseBody = RoleWithPolicies;

// v2 (x-api-version: 2) — adds labels-based RBAC policy links.
// A strict superset of v1: omitting the new arrays leaves existing links unchanged.
export type RoleRbacPolicyLinks = {
  endpointPolicyIds: string[];
  dataPolicyIds: string[];
};

export type RoleWithPoliciesV2 = RoleWithPolicies & RoleRbacPolicyLinks;

const roleRbacPolicyLinksSchemaMap = identity<
  joi.SchemaMap<Partial<RoleRbacPolicyLinks>>
>({
  endpointPolicyIds: joi.array().items(joi.string().pattern(POLICY_ID_PATTERN)),
  dataPolicyIds: joi.array().items(joi.string().pattern(POLICY_ID_PATTERN)),
});

// POST /roles v2
export type RolesPostRequestBodyV2 = RolesPostRequestBody &
  Partial<RoleRbacPolicyLinks>;
export const rolesPostRequestBodySchemaV2 = rolesPostRequestBodySchema.keys(
  roleRbacPolicyLinksSchemaMap,
);
export type RolesPostResponseBodyV2 = RoleWithPoliciesV2;

// GET /roles/:roleId v2
export type RoleGetResponseBodyV2 = RoleWithPoliciesV2;

// PUT /roles/:roleId v2
export type RolePutRequestBodyV2 = RolePutRequestBody &
  Partial<RoleRbacPolicyLinks>;
export const rolePutRequestBodySchemaV2 = rolePutRequestBodySchema.keys(
  roleRbacPolicyLinksSchemaMap,
);
export type RolePutResponseBodyV2 = RoleWithPoliciesV2;
